import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  useUploadPartsExcel,
  type PartExcelUploadResponse,
} from "../services/PartService";
import type { PartGroup } from "../services/PartGroupService";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";
import { useFileDropzone } from "../hooks/useFileDropzone";

interface PartExcelUploadModalProps {
  open: boolean;
  onClose: () => void;
  partGroups: PartGroup[];
}

// Upload responses can carry up to MAX_BATCH_PAYLOAD rows of errors (10k).
// Show enough to diagnose without flooding the modal; the rest are summarized.
const MAX_VISIBLE_ROW_ERRORS = 25;

const ACCEPTED_EXTENSIONS = [".xlsx", ".xlsm", ".csv"];

const PartExcelUploadModal = ({
  open,
  onClose,
  partGroups,
}: PartExcelUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [partGroupFk, setPartGroupFk] = useState<number | "">("");
  const [result, setResult] = useState<PartExcelUploadResponse | null>(null);

  const uploadExcel = useUploadPartsExcel();

  const selectFile = useCallback((selected: File) => {
    setFile(selected);
    setResult(null);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) selectFile(selected);
    },
    [selectFile],
  );

  const { dragActive, dropzoneProps } = useFileDropzone({
    accept: ACCEPTED_EXTENSIONS,
    onFile: selectFile,
    onReject: (rejected) =>
      NucToast({
        message: `Unsupported file "${rejected.name}". Accepted: ${ACCEPTED_EXTENSIONS.join(", ")}`,
        type: "error",
        hideProgressBar: false,
      }),
  });

  const handleSubmit = () => {
    if (!file) return;
    uploadExcel.mutate(
      { file, partGroupFk: partGroupFk === "" ? undefined : partGroupFk },
      {
        onSuccess: (data) => {
          setResult(data);
        },
        onError: (err) => {
          const detail = extractErrorDetail(err);
          console.error("Failed to upload parts file", err);
          NucToast({
            message: `Failed to upload parts file: ${detail}`,
            type: "error",
            hideProgressBar: false,
          });
        },
      },
    );
  };

  const handleClose = () => {
    setFile(null);
    setPartGroupFk("");
    setResult(null);
    onClose();
  };

  // Belt-and-suspenders: if the parent ever toggles `open` to false without
  // routing through handleClose (e.g. a programmatic close), still reset state.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setPartGroupFk("");
      setResult(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={uploadExcel.isLoading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Upload Parts from Excel or CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload an Excel or CSV file with parts data. Required column:{" "}
            <strong>name</strong>. Optional columns: part_type, manufacturer,
            description. Any additional columns will be added as feature sheet
            entries.
          </Typography>

          <Box
            sx={{
              border: "2px dashed",
              borderColor: dragActive || file ? "primary.main" : "grey.300",
              bgcolor: dragActive ? "action.hover" : "transparent",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main" },
            }}
            component="label"
            {...dropzoneProps}
          >
            <input
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(",")}
              hidden
              onChange={handleFileChange}
            />
            <UploadFileIcon
              sx={{ fontSize: 40, color: file ? "primary.main" : "grey.400" }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {dragActive
                ? "Drop file to upload"
                : file
                  ? file.name
                  : "Click or drag to upload Excel or CSV file"}
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Part Group</InputLabel>
            <Select
              value={partGroupFk}
              label="Part Group"
              onChange={(e) => {
                const raw = e.target.value;
                setPartGroupFk(raw === "" ? "" : Number(raw));
              }}
            >
              <MenuItem value="">None</MenuItem>
              {partGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {result && (
            <Alert severity={result.error_count > 0 ? "warning" : "success"}>
              <AlertTitle>
                Created {result.created_count} parts
                {result.error_count > 0 &&
                  ` — ${result.error_count} rows had errors`}
              </AlertTitle>
              {result.error_count > 0 && result.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <List
                    dense
                    disablePadding
                    sx={{ maxHeight: 200, overflowY: "auto" }}
                  >
                    {result.errors
                      .slice(0, MAX_VISIBLE_ROW_ERRORS)
                      .map(({ row, error }) => (
                        <ListItem key={row} disableGutters sx={{ py: 0 }}>
                          <ListItemText
                            primary={`Row ${row}: ${error}`}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontFamily: "monospace" },
                            }}
                          />
                        </ListItem>
                      ))}
                  </List>
                  {result.errors.length > MAX_VISIBLE_ROW_ERRORS && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      …and {result.errors.length - MAX_VISIBLE_ROW_ERRORS} more.
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadExcel.isLoading}>
          {result ? "Done" : "Cancel"}
        </Button>
        {!result && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!file || uploadExcel.isLoading}
            startIcon={
              uploadExcel.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {uploadExcel.isLoading ? "Uploading..." : "Upload"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PartExcelUploadModal;
